import React, { useState, useEffect, useContext } from 'react';
import { SimplifiedChampion } from '../../../../../gameTypes';
import { GameLog } from '../../../Logs/LogsDisplay';
import { DamageSubType } from '../ComboDisplay/Events/utils';

import GameAssetsContext from '../../../../../context/GameAssetContext';
import './CombatDisplay.css';
import ChampionImageWithLevel from '../HealthBar/ChampionImageWithLevel';
import SimpleHealthManaBar from '../HealthBar/SimpleHealthManaBar';

interface CombatDisplayProps {
  champion: SimplifiedChampion;
  targetChampion: SimplifiedChampion | null;
  logs: GameLog[];
  spellImageUrls: Record<string, string>;
  autoAttackImageUrl: string;
  passiveImageUrl: string;
  championImageUrl: string;
  targetChampionImageUrl: string;
}

interface DamageInstance {
  amount: number;
  damageSubType: DamageSubType;
  timestamp: number;
}

interface GroupedCombatEvent {
  id: string;
  abilityKey: string;
  spellImageUrl: string;
  damageInstances: DamageInstance[];
  timestamp: number;
  keyPressTimestamp: number;
}

interface TakedownEvent {
  id: string;
  targetId: string;
  targetName: string;
  timestamp: number;
}

const CombatDisplay: React.FC<CombatDisplayProps> = ({
  champion,
  targetChampion,
  logs,
  spellImageUrls,
  autoAttackImageUrl,
  passiveImageUrl,
  championImageUrl,
  targetChampionImageUrl,
}) => {
  const [activeCombatEvents, setActiveCombatEvents] = useState<GroupedCombatEvent[]>([]);
  const [activeTakedowns, setActiveTakedowns] = useState<TakedownEvent[]>([]);
  const [processedDamageEvents, setProcessedDamageEvents] = useState<Set<number>>(new Set());
  const [processedTakedowns, setProcessedTakedowns] = useState<Set<number>>(new Set());
  const [mountTimestamp] = useState<number>(Date.now());
  const { gameWideAssets, championsAssets } = useContext(GameAssetsContext);

  // Helper function to extract image URL from universal icon function
  const getImageUrlFromUniversalIcon = (iconRequest: any): string | null => {
    const { sourceType, imageState, championName, abilityKey } = iconRequest;
    const { championImages } = require('../ComboDisplay/Events/Images/championImages');
    
    if ((sourceType === 'Ability' || sourceType === 'Passive') && championName) {
      const champMap = championImages[championName];
      if (champMap) {
        let finalIconUrl: string | undefined;

        if (sourceType === 'Ability') {
          // For Ability sources, try imageState first
          if (imageState && imageState !== 'default') {
            finalIconUrl = champMap[imageState.toLowerCase()];
          }
          
          // Try championImages with abilityKey if no imageState result
          if (!finalIconUrl && abilityKey) {
            finalIconUrl = champMap[abilityKey.toLowerCase()];
          }

          // Fallback to championsAssets for abilities
          if (!finalIconUrl && championsAssets[championName]?.spellImageUrls && imageState) {
            finalIconUrl = championsAssets[championName].spellImageUrls[imageState.toUpperCase()];
          }
        }

        if (finalIconUrl) {
          return finalIconUrl;
        }
      }
    }
    
    return null;
  };

  // Debug logging for image URLs
 /*  console.log('CombatDisplay - Champion Image URL:', championImageUrl);
  console.log('CombatDisplay - Target Champion Image URL:', targetChampionImageUrl);
  console.log('CombatDisplay - Champion:', champion?.name);
  console.log('CombatDisplay - Target Champion:', targetChampion?.name);
  console.log('CombatDisplay - Mount Timestamp:', mountTimestamp); */

  // Helper function to get damage type CSS class
  const getDamageTypeClass = (damageSubType: DamageSubType): string => {
    switch (damageSubType) {
      case DamageSubType.Physical:
        return 'damage-physical';
      case DamageSubType.Magic:
        return 'damage-magic';
      case DamageSubType.True:
        return 'damage-true';
      case DamageSubType.AdaptiveDamage:
        return 'damage-physical'; // Default to physical for adaptive
      default:
        return 'damage-physical';
    }
  };

  // Handle new TakeDown events
  useEffect(() => {
    const takedownEvents = logs.filter(log => 
      log.type === 'TakeDown' && 
      log.timestamp >= mountTimestamp
    );
    
    takedownEvents.forEach((takedownLog) => {
      // Skip if we've already processed this event
      if (processedTakedowns.has(takedownLog.timestamp)) return;
      
      // Skip if required details are missing
      if (!takedownLog.details.targetId || !takedownLog.details.targetName) return;
      
      const newTakedown: TakedownEvent = {
        id: `takedown-${takedownLog.timestamp}-${Math.random()}`,
        targetId: takedownLog.details.targetId,
        targetName: takedownLog.details.targetName,
        timestamp: takedownLog.timestamp,
      };

      // Mark this takedown as processed
      setProcessedTakedowns(prev => new Set([...prev, takedownLog.timestamp]));
      
      // Add the new takedown event immediately
      setActiveTakedowns(prev => [...prev, newTakedown]);
      
      // Remove this specific takedown after 1.5 seconds (skull disappears = revive)
      setTimeout(() => {
        setActiveTakedowns(prev => prev.filter(takedown => takedown.id !== newTakedown.id));
      }, 1500);
    });
  }, [logs, processedTakedowns, mountTimestamp]);

  // Handle new DamageTaken events and group them by keypress
  useEffect(() => {
    const damageEvents = logs.filter(log => 
      log.type === 'DamageTaken' && 
      log.details.championId === targetChampion?.id &&
      log.details.attackerId === champion?.id && // Only damage dealt BY this champion
      log.timestamp >= mountTimestamp
    );
    
    // Process new damage events
    const newDamageEvents = damageEvents.filter(log => 
      !processedDamageEvents.has(log.timestamp)
    );

    if (newDamageEvents.length === 0) return;

    // Mark all new damage events as processed
    setProcessedDamageEvents(prev => {
      const newSet = new Set(prev);
      newDamageEvents.forEach(log => newSet.add(log.timestamp));
      return newSet;
    });

    // Group damage events by keypress
    const damagesByKeypress = new Map<number, DamageInstance[]>();

    newDamageEvents.forEach((damageLog) => {
      const damageAmount = damageLog.details.damageAmount || 0;
      if (damageAmount <= 0) return; // Skip zero damage
      
      // Find the most recent KeyPress from THIS champion before this damage event
      const recentKeyPress = logs
        .filter(log => 
          log.type === 'KeyPress' && 
          log.details.championId === champion?.id && // Only keypresses from this champion
          log.timestamp <= damageLog.timestamp &&
          log.timestamp >= damageLog.timestamp - 3000 // Within 3 seconds before damage
        )
        .sort((a, b) => b.timestamp - a.timestamp)[0]; // Most recent first
      
      const keyPressTimestamp = recentKeyPress?.timestamp || damageLog.timestamp;
      
      const damageInstance: DamageInstance = {
        amount: damageAmount,
        damageSubType: damageLog.details.damageSubType || DamageSubType.Physical,
        timestamp: damageLog.timestamp,
      };

      if (!damagesByKeypress.has(keyPressTimestamp)) {
        damagesByKeypress.set(keyPressTimestamp, []);
      }
      damagesByKeypress.get(keyPressTimestamp)!.push(damageInstance);
    });

    // Create grouped combat events
    damagesByKeypress.forEach((damageInstances, keyPressTimestamp) => {
      // Find the keypress to get ability info
      const keyPress = logs.find(log => 
        log.type === 'KeyPress' && 
        log.timestamp === keyPressTimestamp
      );
      
      const abilityKey = keyPress?.details.abilityKey?.toUpperCase() || 'A';
      const imageState = keyPress?.imageState || '';
      const championName = keyPress?.details.championName || '';
      
      let spellImageUrl = '';
      
      // For auto attack, always use the regular auto attack image
      if (abilityKey === 'A') {
        spellImageUrl = autoAttackImageUrl;
      } else {
        // Create the request for getting the image URL
        const iconRequest = {
          sourceType: 'Ability',
          imageState: imageState,
          sourceName: '', // Not needed for abilities
          championName: championName,
          abilityKey: abilityKey
        };
        
        // Get the image URL using our helper function
        const imageUrl = getImageUrlFromUniversalIcon(iconRequest);
        
        // Use the fetched image URL or fallback to default spell images
        spellImageUrl = imageUrl || 
          (abilityKey === 'Q' ? spellImageUrls.Q :
           abilityKey === 'W' ? spellImageUrls.W :
           abilityKey === 'E' ? spellImageUrls.E :
           abilityKey === 'R' ? spellImageUrls.R :
           abilityKey === 'P' ? passiveImageUrl :
           autoAttackImageUrl);
      }

      const newEvent: GroupedCombatEvent = {
        id: `grouped-${keyPressTimestamp}-${Math.random()}`,
        abilityKey,
        spellImageUrl,
        damageInstances: damageInstances.sort((a, b) => a.timestamp - b.timestamp), // Sort by timestamp
        timestamp: Math.min(...damageInstances.map(d => d.timestamp)), // Use earliest damage timestamp
        keyPressTimestamp,
      };

      // Add the new event immediately, maintaining max 4 events
      setActiveCombatEvents(prev => {
        const newEvents = [...prev, newEvent];
        // Keep only the 4 most recent events
        return newEvents.slice(-4);
      });
      
      // Remove this specific event after 2 seconds
      setTimeout(() => {
        setActiveCombatEvents(prev => prev.filter(event => event.id !== newEvent.id));
      }, 2000);
    });
  }, [logs, spellImageUrls, autoAttackImageUrl, passiveImageUrl, processedDamageEvents, mountTimestamp, targetChampion?.id]);

  if (!targetChampion) {
    return (
      <div className="combat-display no-target">
        <div className="champion-mini-card">
          <ChampionImageWithLevel
            imageUrl={championImageUrl}
            name={champion.name}
            level={champion.level}
          />
          <SimpleHealthManaBar
            currentHealth={champion.stats.totalStats.currentHealth}
            maxHealth={champion.stats.totalStats.health}
            shield={champion.stats.totalStats.shield}
            magicShield={champion.stats.totalStats.magicShield}
            physicalShield={champion.stats.totalStats.physicalShield}
            primaryResource={
              champion.stats.primaryResource
                ? {
                    type: champion.stats.primaryResource.type,
                    current: champion.stats.primaryResource.current,
                    max: champion.stats.primaryResource.max
                  }
                : undefined
            }
          />
        </div>
        <div className="no-target-message">
          <span>No target selected</span>
        </div>
      </div>
    );
  }

  return (
    <div className="combat-display">
      {/* Main Champion */}
      <div className={`champion-mini-card attacker ${champion.team}`}>
        <ChampionImageWithLevel
          imageUrl={championImageUrl || '/api/placeholder/64/64'}
          name={champion.name}
          level={champion.level}
        />
        <SimpleHealthManaBar
          currentHealth={champion.stats.totalStats.currentHealth}
          maxHealth={champion.stats.totalStats.health}
          shield={champion.stats.totalStats.shield}
          magicShield={champion.stats.totalStats.magicShield}
          physicalShield={champion.stats.totalStats.physicalShield}
          primaryResource={
            champion.stats.primaryResource
              ? {
                  type: champion.stats.primaryResource.type,
                  current: champion.stats.primaryResource.current,
                  max: champion.stats.primaryResource.max
                }
              : undefined
          }
        />
        <div className="combat-champion-name">{champion.name}</div>
      </div>

      {/* Combat Effects Area */}
      <div className="combat-effects">
        {activeCombatEvents.map((event, index) => (
          <div 
            key={event.id} 
            className="combat-event"
          >
            {/* Spell Icon - shown once per keypress */}
            <div className="spell-flash">
              <img src={event.spellImageUrl} alt={event.abilityKey} />
              <span className="ability-key">{event.abilityKey}</span>
            </div>
            
            {/* Multiple Damage Numbers - one per damage instance */}
            <div className="damage-numbers-container">
              {event.damageInstances.map((damage, damageIndex) => (
                <div 
                  key={`${event.id}-damage-${damageIndex}`}
                  className={`damage-number ${getDamageTypeClass(damage.damageSubType)}`}
                  style={{ animationDelay: `${damageIndex * 0.1}s` }} // Stagger animations slightly
                >
                  -{Math.round(damage.amount)}
                </div>
              ))}
            </div>
          </div>
        ))}
        
        {activeCombatEvents.length === 0 && (
          <div className="vs-indicator">VS</div>
        )}
      </div>

      {/* Target Champion */}
      <div className={`champion-mini-card target ${targetChampion.team} ${activeTakedowns.some(takedown => takedown.targetId === targetChampion.id) ? 'dead' : ''}`}>
        <div className="champion-image-container">
          <ChampionImageWithLevel
            imageUrl={targetChampionImageUrl || '/api/placeholder/64/64'}
            name={targetChampion.name}
            level={targetChampion.level}
          />
          {/* Skull overlay for takedowns */}
          {activeTakedowns.some(takedown => takedown.targetId === targetChampion.id) && (
            <div className="skull-overlay">
              💀
            </div>
          )}
        </div>
        <SimpleHealthManaBar
          currentHealth={targetChampion.stats.totalStats.currentHealth}
          maxHealth={targetChampion.stats.totalStats.health}
          shield={targetChampion.stats.totalStats.shield}
          magicShield={targetChampion.stats.totalStats.magicShield}
          physicalShield={targetChampion.stats.totalStats.physicalShield}
          primaryResource={
            targetChampion.stats.primaryResource
              ? {
                  type: targetChampion.stats.primaryResource.type,
                  current: targetChampion.stats.primaryResource.current,
                  max: targetChampion.stats.primaryResource.max
                }
              : undefined
          }
        />
        <div className="combat-champion-name">{targetChampion.name}</div>
      </div>
    </div>
  );
};

export default CombatDisplay; 